import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Box, Typography, Chip, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, IconButton, DialogActions, Button,
} from '@mui/material'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import { getWebhookEvents } from '../../api/client'
import type { WebhookEvent } from '../../api/types'

export default function WebhooksPage() {
  const [selected, setSelected] = useState<WebhookEvent | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['webhook-events'],
    queryFn: () => getWebhookEvents(50),
    refetchInterval: 5_000,
  })

  const events = data?.data ?? []

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} mb={2}>Webhook Events</Typography>

      {isLoading ? (
        <CircularProgress />
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Source</TableCell>
                <TableCell>Event Type</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Received</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {events.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center">No events received yet</TableCell></TableRow>
              ) : events.map(e => (
                <TableRow key={e.id}>
                  <TableCell><Chip label={e.source.toUpperCase()} size="small" variant="outlined" /></TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{e.eventType}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>{e.id}</TableCell>
                  <TableCell>{new Date(e.receivedAt).toLocaleString()}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => setSelected(e)}>
                      <ContentPasteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="md">
        <DialogTitle>
          {selected?.source.toUpperCase()} — {selected?.eventType}
          <Typography variant="caption" display="block" color="text.secondary">{selected?.id}</Typography>
        </DialogTitle>
        <DialogContent>
          <Box
            component="pre"
            sx={{ fontSize: 12, overflow: 'auto', background: '#f5f5f5', p: 2, borderRadius: 1 }}
          >
            {selected ? formatJson(selected.payload) : ''}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

function formatJson(raw: string): string {
  try { return JSON.stringify(JSON.parse(raw), null, 2) }
  catch { return raw }
}
